import type { WebhookAttempt } from '@compass/client/types/webhook';
import React from 'react';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { cn } from '@/lib/utils';

import { attemptOutcomeLabel, EMPTY_VALUE, formatLatencyMs, isSuccessfulOutcome } from '../utils';

/** Every HTTP attempt, newest first: time, status code, latency and outcome. */
export function AttemptTimeline({ attempts }: { attempts: WebhookAttempt[] }) {
  if (attempts.length === 0) {
    return <p className="text-sm text-muted-foreground">No attempts yet.</p>;
  }
  const ordered = [...attempts].sort((a, b) => (b.number ?? 0) - (a.number ?? 0));
  return (
    <ol className="relative space-y-3 border-l pl-4">
      {ordered.map(attempt => {
        const ok = isSuccessfulOutcome(attempt.outcome);
        return (
          <li key={`${attempt.number}-${attempt.started_at}`} className="relative">
            <span
              className={cn(
                'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background',
                ok ? 'bg-primary' : 'bg-destructive',
              )}
              aria-hidden
            />
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
              <span className="font-medium">
                Attempt {attempt.number ?? EMPTY_VALUE}
                <span
                  className={cn('ml-2 font-normal', ok ? 'text-foreground' : 'text-destructive')}
                >
                  {attempt.status_code ? `HTTP ${attempt.status_code}` : 'No response'}
                </span>
              </span>
              <FormattedDateTime
                value={attempt.started_at}
                className="text-xs text-muted-foreground"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {attemptOutcomeLabel(attempt.outcome)} · {formatLatencyMs(attempt.latency_ms)}
              {attempt.response_size_bytes
                ? ` · ${attempt.response_size_bytes.toLocaleString('en-US')} B response`
                : ''}
            </p>
          </li>
        );
      })}
    </ol>
  );
}
